import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Screen, ScreenTitle, PrimaryButton, ErrorNote } from '../ui/Screen.jsx'
import { FloatField } from '../ui/Field.jsx'
import { countryOptions } from '../platformLists.js'
import { register } from '../auth.js'
import { useConfigBundle, screensOf } from '../config.js'
import { NavWordmark } from '../ui/NavBar.jsx'

/**
 * Registration: email, password, date of birth, country (build spec s2).
 *
 * Minimum age is 13, self-signed, with no guardian or assent step (build spec
 * s8.5). Country is not free text — it keys the crisis resource list, so it
 * comes from config.
 *
 * Next stop is consent, then install, then notification permission, in that
 * order and for reasons that matter (FE-5).
 */
const MIN_AGE = 18   // must match config/demographics.json demo_age.min
const MIN_PASSWORD = 8

export default function Register() {
  const { data: bundle } = useConfigBundle()
  const copy = screensOf(bundle)
  const navigate = useNavigate()
  // ISO 3166-1 alpha-2, resolved by the platform. config ships no list on
  // purpose: the crisis-resource file only names four countries, and using it
  // here would have made every other country unregisterable.
  const countries = countryOptions()

  /*
   * DOB IS ONE MASKED FIELD. The native date input could not be saved (the
   * calendar and ghost text are browser chrome), and the three-field split
   * that replaced it read as a form within a form - legend, example line,
   * three boxes for one fact. What a date of birth actually is, is TYPED:
   * one field, digits only, the slashes appear as she types (31/03/1992),
   * and the expected order lives in the label instead of a subtext line.
   * Numeric keypad via inputMode; bday autofill still works - the browser
   * fills an ISO or local string and the digit-strip normalises either.
   */
  const [form, setForm] = useState({ email: '', password: '', country: '' })
  const [dobDigits, setDobDigits] = useState('')
  /*
   * THE SLASH WRITES ITSELF the moment a segment completes: "31" renders as
   * "31/" immediately, not once the month starts. The trailing separator is
   * what tells her the field took the segment and moved on.
   */
  const dobDisplay =
    dobDigits.length <= 2
      ? dobDigits + (dobDigits.length === 2 ? '/' : '')
      : dobDigits.length <= 4
        ? `${dobDigits.slice(0, 2)}/${dobDigits.slice(2)}` + (dobDigits.length === 4 ? '/' : '')
        : `${dobDigits.slice(0, 2)}/${dobDigits.slice(2, 4)}/${dobDigits.slice(4)}`
  const onDob = (e) => {
    const v = e.target.value
    let digits = v.replace(/[^0-9]/g, '')
    /*
     * THE BACKSPACE TRAP, and every masked input has it: deleting a trailing
     * auto-slash removes only the slash, the digits are unchanged, the
     * reformat paints the slash straight back, and the cursor is stuck. If
     * the text got shorter but the DIGITS did not, she deleted a separator -
     * take the digit with it, which is what backspace means to her.
     */
    if (digits === dobDigits && v.length < dobDisplay.length) digits = digits.slice(0, -1)
    /*
     * SMART PADDING. No day starts with 4-9 and no month starts with 2-9, so
     * a leading digit that cannot open a valid segment IS the whole segment:
     * typing 4/3/1992 becomes 04/03/1992 without her ever typing a zero.
     * Only ever applied to a digit that could not otherwise be valid, so it
     * never second-guesses a real "12" or "31".
     */
    if (digits.length >= 1 && digits[0] > '3') digits = '0' + digits
    if (digits.length >= 3 && digits[2] > '1') digits = digits.slice(0, 2) + '0' + digits.slice(2)
    setDobDigits(digits.slice(0, 8))
  }
  const dobIso = () => {
    if (dobDigits.length !== 8) return ''
    return `${dobDigits.slice(4, 8)}-${dobDigits.slice(2, 4)}-${dobDigits.slice(0, 2)}`
  }
  const [errors, setErrors] = useState({})
  const [failure, setFailure] = useState(null)
  const [busy, setBusy] = useState(false)

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  function validate() {
    const next = {}
    if (!form.email.trim()) next.email = 'Enter your email address.'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      next.email = 'That does not look like an email address.'

    if (form.password.length < MIN_PASSWORD)
      next.password = `Use at least ${MIN_PASSWORD} characters.`

    const iso = dobIso()
    if (!iso) next.dob = 'Enter your date of birth.'
    else {
      /*
       * ORDERED MOST-SPECIFIC FIRST, because "that does not look right" on a
       * date she can see perfectly well reads as the form being broken. The
       * one genuinely common mistake - a day the month does not have - names
       * the month and its real length. new Date(y, m, 0) is the last day of
       * month m, which handles 28/29/30/31 and leap years in one expression.
       */
      const dd = Number(dobDigits.slice(0, 2))
      const mm = Number(dobDigits.slice(2, 4))
      const yyyy = Number(dobDigits.slice(4, 8))
      const now = new Date()
      const lastDay = mm >= 1 && mm <= 12 ? new Date(yyyy, mm, 0).getDate() : 0
      const monthName = mm >= 1 && mm <= 12
        ? new Date(yyyy, mm - 1, 1).toLocaleString('en-GB', { month: 'long' })
        : ''
      if (mm < 1 || mm > 12) next.dob = 'Months go from 01 to 12.'
      else if (dd < 1) next.dob = 'That day does not look right.'
      else if (dd > lastDay) next.dob = `${monthName} ${yyyy} only has ${lastDay} days.`
      else if (yyyy < 1900) next.dob = 'Please check the year.'
      else if (yyyy > now.getFullYear()) next.dob = 'That date is in the future.'
      else {
        const age = ageOn(iso)
        if (age === null) next.dob = 'That date does not look right.'
        else if (age < MIN_AGE) next.dob = `You need to be ${MIN_AGE} or older to take part.`
      }
    }

    if (!form.country) next.country = 'Choose your country.'

    setErrors(next)
    return Object.keys(next).length === 0
  }

  async function submit(e) {
    e.preventDefault()
    setFailure(null)
    if (!validate()) return
    setBusy(true)
    try {
      // Field names are backend's (decision #19): {email, password, dob, country}.
      await register({
        email: form.email.trim(),
        password: form.password,
        dob: dobIso(),
        country: form.country,
      })
      navigate('/consent', { replace: true })
    } catch (err) {
      setFailure(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Screen nav={{ left: <NavWordmark /> }} action={<PrimaryButton type="submit" form="register" disabled={busy || !countries.length}>{busy ? 'Creating your account…' : 'Continue'}</PrimaryButton>}>
      {/*
        THE FIRST WORDS IN THE STUDY (DS-6 §5, Appendix D).

        This said "This takes a couple of minutes." Measured against six gates
        and a 27-screen battery it was false, and it was the FIRST thing the app
        said — DS-4 killed the same claim on the beep for the same reason. A
        time estimate we cannot keep, said first, is the worst possible opening,
        because trust at consent is decided before she reaches consent and this
        screen is everything she has met by then.

        What consent needs from the screen before it is not persuasion but
        SHAPE: what is about to happen, in order, honestly. Six steps and a long
        first session is not a discouraging thing to say; it is the thing that
        makes the rest feel like it was told straight.

        Appendix D, obeyed literally. Must be true: what the five days involve,
        and that consent comes next and can be declined. Must not appear: any
        duration (a count of questions is checkable, a duration is a guess about
        her); anything about mental health or the study's subject matter, since
        this screen is reachable before consent and may be read over a shoulder;
        and any welcome, thanks or encouragement.
      */}
      <ScreenTitle>Create your account</ScreenTitle>

      {/*
        config's words now (CFG-30 screens.registration_opening), replacing the
        version I wrote to design's fact list while these screens were still
        ungoverned. One difference worth noting rather than reverting: mine gave
        a question count and config's does not, because the number varies by
        participant and is not known at this point. A count I cannot stand
        behind is the same defect as a duration, one step quieter.
      */}
      <p className="mb-6 text-base leading-relaxed text-[var(--ink-700)]">
        {copy?.registration_opening?.body ??
          'Today there is one longer set of questions. After that, the app asks six short check-ins a day for five days. The next screen explains what taking part involves, and you can decline it.'}
      </p>

      <form id="register" onSubmit={submit} noValidate>
        <FloatField
          id="email"
          label="Email address"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          value={form.email}
          onChange={set('email')}
          error={errors.email}
        />

        <FloatField
          id="password"
          label="Password"
          hint={`At least ${MIN_PASSWORD} characters.`}
          error={errors.password}
          type="password"
          autoComplete="new-password"
          value={form.password}
          onChange={set('password')}
        />

        <FloatField
          id="dob"
          label="Date of birth (DD/MM/YYYY)"
          inputMode="numeric"
          autoComplete="bday"
          value={dobDisplay}
          onChange={onDob}
          error={errors.dob}
        />

        {countries.length ? (
          <FloatField
            as="select"
            id="country"
            label="Country"
            error={errors.country}
            value={form.country}
            onChange={set('country')}
          >
            <option value="">Choose one</option>
            {countries.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </FloatField>
        ) : (
          <div className="mb-4">
            <p className="rounded-[var(--r-surface)] bg-[var(--surface-sunken)] px-3 py-2 text-sm text-[var(--ink-900)]">
              The country list has not loaded. Please try again shortly.
            </p>
          </div>
        )}

        <ErrorNote error={failure} />

      </form>

      <p className="mt-6 text-center text-sm text-[var(--ink-700)]">
        Already registered?{' '}
        <Link to="/login" className="inline-flex min-h-[max(44px,2.75rem)] items-center px-[var(--s-2)] font-semibold text-[var(--accent)] underline">
          Sign in
        </Link>
      </p>
    </Screen>
  )
}

/** Whole years old today, or null if the date is unparseable or in the future. */
export function ageOn(isoDate, today = new Date()) {
  const dob = new Date(`${isoDate}T00:00:00`)
  if (Number.isNaN(dob.getTime())) return null
  if (dob > today) return null
  let age = today.getFullYear() - dob.getFullYear()
  const beforeBirthday =
    today.getMonth() < dob.getMonth() ||
    (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())
  if (beforeBirthday) age -= 1
  return age
}
